import React, { useEffect, useState, Suspense, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios';

import Navbar from "../components/navbar";
import Footer from "../components/footer";
import PropertyCard from "../components/propertycard";
import PropertySearch from "../components/propertysearch";
import PageHeader from "../components/pageheader";
import InfiniteScroll from 'react-infinite-scroll-component';
const MapComponent = React.lazy(() => import('../components/GoogleMap'));

// const apiPath = `${process.env.REACT_APP_API_URL}api/DimoraProperties/search`;
const apiPath = `${process.env.REACT_APP_API_URL}api/Properties/search`;

const rentalOptions = [
    {
        rentalType: 0,
        rentalTypeDisplay: 'Unfurnished',
        searchParam: 'U'
    },
    {
        rentalType: 1,
        rentalTypeDisplay: 'Furnished',
        searchParam: 'F'
    },
    {
        rentalType: 2,
        rentalTypeDisplay: 'Semi-Furnished',
        searchParam: 'SF'
    }
];

function removeDuplicates(arr) {
    const uniqueObjects = [];
    const seenObjects = new Set();

    for (const obj of arr) {
        const stringOfObj = JSON.stringify(obj);
        if (!seenObjects.has(stringOfObj)) {
            uniqueObjects.push(obj);
            seenObjects.add(stringOfObj);
        }
    }
    return uniqueObjects;
}

// Skeleton Loader component for property cards
const PropertyCardSkeleton = () => {
    return (
        <div className="col-xl-3 col-lg-4 col-sm-6 col-12">
            <div className="card property-card rounded-3 border-0 shadow overflow-hidden">
                <div className="skeleton-image"></div>
                <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="skeleton-text skeleton-price"></div>
                        <div className="skeleton-badge"></div>
                    </div>
                    <div className="skeleton-text skeleton-title mb-3"></div>
                    <div className="skeleton-text skeleton-address mb-3"></div>
                    <div className="d-flex justify-content-between flex-wrap gap-2">
                        <div className="skeleton-text skeleton-detail"></div>
                        <div className="skeleton-text skeleton-detail"></div>
                        <div className="skeleton-text skeleton-detail"></div>
                    </div>
                    <div className="d-flex mt-3 gap-2">
                        {[...Array(3)].map((_, index) => (
                            <div key={index} className="skeleton-amenity"></div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Map Skeleton Loader
const MapSkeleton = () => {
    return (
        <div className="skeleton-map rounded-3 overflow-hidden">
            <div className="skeleton-map-content"></div>
        </div>
    );
};

// Custom Property Loader for InfiniteScroll
const PropertyLoader = () => {
    return (
        <div className="row g-4 mt-0">
            {[...Array(4)].map((_, index) => (
                <PropertyCardSkeleton key={index} />
            ))}
        </div>
    );
};

export default function PropertyListing() {
    const location = useLocation();
    const navigate = useNavigate();

    const searchParams = new URLSearchParams(location.search);
    const propertySearchTypeURLParam = searchParams.get('propertyType');

    const [properties, setProperties] = useState([]);
    const [mapMarkers, setMapMarkers] = useState([]);
    const [propertySearchType, setPropertySearchType] = useState(propertySearchTypeURLParam);
    const [propertyBedrooms, setPropertyBedrooms] = useState();
    const [propertyBathrooms, setPropertyBathrooms] = useState();
    const [propertyAddress, setPropertyAddress] = useState();
    const [propertyAvailability, setPropertyAvailability] = useState()
    const [propertyPerking, setPropertyPerking] = useState();
    const [selectedMapCoords, setSelectedMapCoords] = useState({ lat: 0, lng: 0 });
    const [showLoader, setShowLoader] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(4);
    const [hasMore, setHasMore] = useState(true);
    const [thumbnailsCache, setThumbnailsCache] = useState({});

    const handleReload = () => {
        navigate('/temp', { replace: true });
        navigate(`${location.pathname}${location.search}`, { replace: true });
    };

    const handleSelectedLocationChanged = (data) =>
        setPropertyAddress(data);

    const handleSelectedBedroomsChanged = (data) =>
        setPropertyBedrooms(data);

    const handleSelectedBathroomsChanged = (data) =>
        setPropertyBathrooms(data);

    const handleSelectedPropertyTypeChanged = (data) =>
        setPropertySearchType(data);

    const handleSelectedPropertyAvailabilityChanged = (data) =>
        setPropertyAvailability(data);

    const handleSelectedPerkingChanged = (data) =>
        setPropertyPerking(data);

    const handlePropertyClicked = data => {
        if (typeof data === 'undefined') return;
        setSelectedMapCoords(prevState => ({ ...prevState, ...data }))
    }

    const fetchProperties = async (pageNumber = 1) => {
        try {
            setShowLoader(true);
            // const payload = {
            //     rentalType: propertySearchType || '',
            //     location: propertyAddress || '',
            //     bedrooms: propertyBedrooms || 0,
            //     bathrooms: propertyBathrooms || 0,
            //     availability: propertyAvailability || 0,
            //     parking: propertyPerking || 0,
            //     minPrice: 0,
            //     maxPrice: 10000,
            //     sortBy: 3,
            //     sortDescending: true,
            //     pageSize,
            //     pageNumber
            // };


            const payload = {
                location: propertyAddress || "",
                ...(propertyAvailability === 1 ? { available: true } :
                    propertyAvailability === 2 ? { available: false } : {}),
                ...(propertyBedrooms > 0 ? {
                    minBeds: propertyBedrooms,
                    maxBeds: propertyBedrooms
                } : {}),
                ...(propertyBathrooms > 0 ? {
                    minBaths: propertyBathrooms,
                    maxBaths: propertyBathrooms
                } : {}),
                ...((propertySearchType && propertySearchType.length > 0) ? { rentalType: rentalOptions.find((option)=>option.searchParam===propertySearchType).rentalType } : {}),
                ...(propertyPerking ? { parkingType: propertyPerking } : {}),
                page: pageNumber,
                pageSize: pageSize,
                sortBy: "availableDt",
                sortOrder: "asc"
            }

            const res = await axios.post(apiPath, payload);
            const data = res.data.properties;

            if (data.length < pageSize) {
                setHasMore(false);
            }

            if (data.length > 0) {
                if (pageNumber === 1) {
                    // First page - replace properties
                    setProperties(data);
                } else {
                    // Subsequent pages - append properties
                    setProperties(prev => [...prev, ...data]);
                }
                setPage(prev => prev + 1);
            }

            // setProperties(prev => [...prev, ...data]);

            // setPage(prev => prev + 1);
            fetchThumbnails(data);

            // if (data.length < pageSize) {
            //     setHasMore(false);
            // }
        } catch (error) {
            console.error(error);
        } finally {
            setShowLoader(false);
            setInitialLoading(false);
        }
    };

    const fetchThumbnails = async (newProps) => {
        const newIds = newProps.filter(p => !thumbnailsCache[p.id]).map(p => p.id);

        // Don't await - let thumbnails load in background
        newIds.forEach(async (id) => {
            try {
                // const url = `${process.env.REACT_APP_API_URL}api/Images/property/${id}/thumbnail?width=301&height=225&quality=80`;
                const url = `${process.env.REACT_APP_API_URL}api/Properties/${id}/thumbnail?width=301&height=225&quality=80`;
                const res = await axios.get(url, { responseType: 'blob' });
                const thumbnailUrl = URL.createObjectURL(res.data);

                // Update cache when thumbnail loads
                setThumbnailsCache(prev => ({ ...prev, [id]: thumbnailUrl }));
            } catch (error) {
                console.error(`Failed to load thumbnail for property ${id}:`, error);
                // Set null to indicate failed load
                setThumbnailsCache(prev => ({ ...prev, [id]: null }));
            }
        });
    };

    useEffect(() => {
        setPage(1);
        setProperties([]);
        setHasMore(true);
        setInitialLoading(true);
        fetchProperties(1);
    }, [propertyAddress, propertyBathrooms, propertyBedrooms, propertySearchType, propertyAvailability, propertyPerking]);

    const markers = useMemo(() => {
        const markerList = properties.map(item => ({
            id: item.id,
            key: item.address,
            location: { lat: item.lat, lng: item.lng },
            beds: item.beds,
            baths: item.baths,
            city: item.city,
            price: item.price
        }));
        return markerList;
        // return removeDuplicates(markerList);
    }, [properties]);

    useEffect(() => {
        setMapMarkers(markers);
    }, [markers]);


    return (
        <>
            <Navbar navClass="defaultscroll sticky" menuClass="navigation-menu nav-right" />
            <PageHeader />
            <section className='mb-md-5 mb-2'>
                <div className="container-fluid">
                    <div className="row g-3">


                        <div className="col-12">
                            <PropertySearch
                                onSelectedPropertyTypeChanged={handleSelectedPropertyTypeChanged}
                                onSelectedLocationChanged={handleSelectedLocationChanged}
                                onSelectedBedroomsChanged={handleSelectedBedroomsChanged}
                                onSelectedBathroomsChanged={handleSelectedBathroomsChanged}
                                onSelectedAvailabilityChanged={handleSelectedPropertyAvailabilityChanged}
                                onSelectedPerkingChanged={handleSelectedPerkingChanged}
                            />
                        </div>
                        <div className="col-xxl-8 col-xl-9 col-lg-8 col-md-7 col-12 mt-0 mb-3">
                            {/* HOUSE CARDS */}
                            {initialLoading ? (
                                <div className="row g-4 mt-0">
                                    {[...Array(8)].map((_, index) => (
                                        <PropertyCardSkeleton key={index} />
                                    ))}
                                </div>
                            ) : (
                                <InfiniteScroll
                                    dataLength={properties.length}
                                    next={() => fetchProperties(page)}
                                    hasMore={hasMore}
                                    loader={<PropertyLoader />}
                                    scrollThreshold={0.3} // Triggers next() at 30% scroll
                                >
                                    <div className="row g-4 mt-0" onMouseLeave={() => setSelectedMapCoords({ lat: 0, lan: 0 })}>
                                        {properties.length > 0 ? properties?.map((item) => (
                                            <PropertyCard
                                                key={item.id}
                                                item={item}
                                                onPropertyClicked={handlePropertyClicked}
                                                onMouseEnter={() => handlePropertyClicked(item)}
                                                thumbNails={[{ id: item.id, thumbnailUrl: thumbnailsCache[item.id] || null }]}
                                            />
                                        )) : <div className='d-flex flex-column justify-content-center align-items-center gap-2 mt-5'>
                                            <h4 className="fw-bold">No Properties Found</h4>
                                            <p className="text-secondary mb-4 text-center fs-7">
                                                We couldn't find any properties that match your current filters.
                                                <br />Try adjusting your search criteria for better results.
                                            </p></div>}
                                    </div>
                                </InfiniteScroll>
                            )}
                        </div>

                        {/* MAP COLUMN */}
                        <div className="col-xxl-4 col-xl-3 col-lg-4 col-md-5 col-12">
                            <div className="card map border-0 sticky-bar-400">
                                <div className="card-body p-0 rounded-3 overflow-hidden">
                                    {initialLoading ? (
                                        <MapSkeleton />
                                    ) : mapMarkers?.length > 0 ? (
                                        <Suspense fallback={<MapSkeleton />}>
                                            <MapComponent markers={mapMarkers} selectedMapCoords={selectedMapCoords} />
                                        </Suspense>
                                    ) : null}

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <div className="position-relative">
                <div className="shape overflow-hidden text-white">
                    <svg viewBox="0 0 2880 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z" fill="currentColor"></path>
                    </svg>
                </div>
            </div>
            <Footer />

            {/* Custom CSS for skeleton loader */}
            <style jsx>{`
                /* Base skeleton animation */
                @keyframes skeleton-loading {
                    0% {
                        background-position: -200px 0;
                    }
                    100% {
                        background-position: calc(200px + 100%) 0;
                    }
                }
                
                /* Common skeleton styles */
                .skeleton-text,
                .skeleton-image,
                .skeleton-badge,
                .skeleton-amenity,
                .skeleton-map-content {
                    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                    background-size: 200px 100%;
                    animation: skeleton-loading 1.5s infinite linear;
                    border-radius: 4px;
                }
                
                /* Text skeletons */
                .skeleton-text {
                    height: 16px;
                    width: 100%;
                }
                
                .skeleton-price {
                    height: 24px;
                    width: 100px;
                }
                
                .skeleton-badge {
                    height: 24px;
                    width: 60px;
                    border-radius: 12px;
                }
                
                .skeleton-title {
                    height: 20px;
                    width: 80%;
                }
                
                .skeleton-address {
                    height: 16px;
                    width: 70%;
                }
                
                .skeleton-detail {
                    height: 16px;
                    width: 80px;
                }
                
                .skeleton-amenity {
                    height: 24px;
                    width: 70px;
                    border-radius: 12px;
                }
                
                /* Images skeletons */
                .skeleton-image {
                    height: 225px;
                    width: 100%;
                    border-radius: 4px 4px 0 0;
                }
                
                /* Map skeleton */
                .skeleton-map {
                    height: 100%;
                    min-height: 400px;
                    width: 100%;
                }
                
                .skeleton-map-content {
                    height: 100%;
                    min-height: 400px;
                    width: 100%;
                    border-radius: 0;
                }
                
                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .skeleton-image {
                        height: 180px;
                    }
                    
                    .skeleton-map, .skeleton-map-content {
                        min-height: 300px;
                    }
                }
                
                @media (max-width: 576px) {
                    .skeleton-image {
                        height: 150px;
                    }
                    
                    .skeleton-map, .skeleton-map-content {
                        min-height: 250px;
                    }
                }
            `}</style>
        </>
    );
}